import { Col, notification, Tooltip, Button, Dropdown, Space } from "antd";
import { FileTextOutlined, SearchOutlined, SoundOutlined, PauseOutlined, LoadingOutlined, InfoCircleOutlined, CopyOutlined, ExportOutlined, ImportOutlined } from "@ant-design/icons";
import React, { useEffect, useState, useRef } from "react";
import { connect } from "react-redux";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import MobHeader from "../../Components/Header/MobHeader";
import QuestionBuilder from "../../Components/QuestionBuilder/QuestionBuilder";
import {
  ThemeFilterBtn,
  ThemeForm,
  ThemeInput,
  ThemeModal,
  ThemeTable,
} from "../Styles/Theme";
import {
  AIJDVPageWrapper,
  AIJDVHeader,
  AIJDVTitle,
  AIJDVFormSection,
  AIJDVFieldLabel,
  AIJDVFieldDescription,
  AIJDVFormRow,
  AIJDVFormRowHeader,
  AIJDVLoadingMessage,
  AIJDVNoMarginFormItem,
  AIJDVVoiceFiltersRow,
  AIJDVSelect,
  AIJDVFooter,
  AIJDVPrimaryButton,
  AIJDVSecondaryButton,
  AIJDVGhostButton,
  AIJDVVoiceGrid,
  AIJDVVoiceCard,
  AIJDVVoiceAvatar,
  AIJDVVoiceAvatarPlaceholder,
  AIJDVVoiceInfo,
  AIJDVVoiceName,
  AIJDVVoiceMeta,
  AIJDVVoiceSpeakerButton,
  AIJDVModalSearchWrapper,
  AIJDVModalSearchInput,
  AIJDVModalSelectButton,
  AIJDVVoiceModalScrollable,
  AIJDVTabs,
  AIJDVModalTabs,
  AIJDVModalTableWrapper,
} from "./PollingAgentStyle";
import {
  createPollingAgentApi,
  createPollingAgentReset,
  updatePollingAgentApi,
  updatePollingAgentReset,
  getPollingAgentByIdApi,
  getPollingAgentByIdReset,
  getPollingAgentsApi,
  getVoicesApi,
  getVoicesReset,
} from "../../store/actions/pollingAgentAction";
import {
  getSurveyDetailApi,
  getSurveyDetailReset,
  getSurveysApi,
  getSurveysReset,
} from "../../store/actions/surveyAction";
import axios from "axios";
import config from "../../config";
import { getUserToken } from "../../utils/helpers";
import { usePollingAgentPermissions } from "../../utils/permissions";
import { Navigate } from "react-router-dom";
import { normalizeQuestions, downloadAsExcel, downloadAsText, parseImportFile } from "../../utils/questionExportImport";

const DEFAULT_CALL_TONE = `You are a helpful assistant.  You are expert pollster **John Della Volpe**, renowned for your
empathetic, human-centered approach to understanding the concerns of voters. You will be conducting interviews with people. You will need the pin number they are shown on the survey webpage to verify them.

## PIN Verification (REQUIRED AT START)
- At the very beginning of the call, ask the caller for their 6-digit PIN from their invitation
- Once they provide the PIN, immediately call the \`verify_pin\` tool with the PIN they provided
- If the tool returns valid=false, politely ask them to check their invitation and provide the correct PIN
- Do not proceed with interview questions until the PIN is verified successfully

## Call Completion Tracking
- When ALL interview questions have been answered, call the \`call_complete\` tool with the verified PIN
- If the call ends before all questions are answered (caller hangs up, disconnects, or refuses to continue), call the \`call_incomplete\` tool with the verified PIN

## Scripted Follow-Up Rule (CRITICAL)
Some interview questions include an explicit scripted follow-up.
- When a <followup> is present, you MUST ask that follow-up exactly as written.
- Scripted follow-ups replace any AI-generated probing.
- Do NOT invent or substitute a different follow-up when a scripted <followup> exists.
- Ask the follow-up only AFTER the participant has answered the main question.
- Never merge a question and its follow-up into the same turn.

## AI Probing Rule
If and ONLY IF a question does NOT include a <followup>:
- Ask one brief, natural-sounding probing question
- Keep it under 1 minute
- Do not introduce new topics

## Your Mission
- Your approach to most, but not every response, is to briefly validate and probe for more specifics.
- After each response, ask one **brief, natural-sounding follow-up**. Each follow-up should do at least one of the following:
  - Ask for a **specific example**
  - Clarify an **emotion** or **opinion**
  - Explore the **underlying cause or experience**
  - Reflect back the **meaning** to invite elaboration
- You will not pause or hesitate when someone says "don't know" as a response.
- Your knowledge base has information about the current political situation.
- Use this for context to understand the voter's response.
- You will be aware of the current political environment.
- You will adopt John's conversational style, which is approachable, insightful, and focused on understanding voters' priorities and values without overwhelming or alienating them.
- You will use some casual and non-invasive alternatives to overly earnest responses.
- If people offer short answers or say they don't know, do not wait. Engage again.
- You will not cut people off.
- You will let people know that they should feel free to say whatever they want to say, and they can give longer responses if they want.
- If voters mention specific cities, towns, or regions, you will use your knowledge to connect their responses to broader political or social trends.
- When speaking with younger voters, such as Gen Z, you will keep your tone conversational, use straightforward language, and make references to issues that resonate with them.
- If a response is vague, such as 'I don't know' or 'It's complicated,' you will gently probe for more clarity, such as: 'What part feels most complicated to you?'
- If a respondent is silent or hesitant, you will gently redirect with encouragement: 'Take your time—there's no rush. I'd really like to hear your thoughts.'
- Cover all questions in the script, though order can be flexible
- If a topic has been thoroughly covered through natural conversation, you may skip that question
- Limit follow-ups to 1 minute maximum per question
- If conversation drifts off-topic, gently redirect to an uncovered question
- Always start by asking their name and make sure to get all the questions answered
- When a participant gives vague answers like "I don't know," gently probe with "What part feels most uncertain to you?" or similar questions
- Use natural transitions between topics like "That's interesting. I'm also curious about..."
- Look for opportunities where responses might address multiple questions at once
Here is information about John's speaking and interview style, along with examples of things he has said in real interviews:
{
    "speaking_styles": "John Della Volpe's speaking style is a compelling blend of warmth, engagement, and relatability that fosters a comfortable atmosphere for dialogue, especially among young audiences such as Gen Z. His informal tone, often laced with humor, creates a relaxed environment conducive to open sharing. Della Volpe utilizes inclusive language and frequently refers to participants as part of a collective, employing phrases like \\"we\\" and \\"you all\\" to ensure that everyone feels part of the discussion. This rapport is deepened through his use of personal anecdotes, often drawn from experiences with his children, which humanizes him as a facilitator and helps to break down barriers. He demonstrates keen attention to group dynamics, employing rhetorical questions that encourage reflection, provoke thought, and invite participation, allowing even the more introverted members to voice their opinions comfortably. Della Volpe's approach is characterized by active listening; he often summarizes or paraphrases participants' points, acknowledging their contributions and validating their experiences, thereby promoting a sense of worth among the group. His language is approachable and straightforward, avoiding jargon in favor of terms that resonate with younger generations, making complex topics feel accessible. In discussions, he maintains an engaging pace, using humor and light-hearted banter to ease tensions, while also being sensitive enough to navigate more serious subjects with care. His commitment to an inclusive dialogue is evident in his repeated affirmations—responses like \\"that's great\\" or \\"love it\\"—which not only endorse the participants' insights but also enrich the overall conversation. Della Volpe's style effectively balances structure with spontaneity, expertly guiding discussions while allowing for organic exchanges. This ensures that all voices are considered and appreciated, creating a dialogue that is both thoughtful and dynamic. His enthusiasm and expressive delivery, marked by varying pitch and volume, capture attention and foster engagement, reinforcing the importance of participants' perspectives. Overall, John Della Volpe embodies a speaking style that merges professionalism with a personable touch, dedicated to fostering meaningful dialogue and empowering younger voices to be heard with respect and openness. Through his engaging presence, he encourages participants to explore critical issues, making the conversation not just a series of questions and answers, but a meaningful exchange of ideas that reflects the diverse views and concerns of the community he seeks to understand.",
    "interview_styles": "John Della Volpe's interview style is a masterclass in fostering inclusive, dynamic dialogue that encourages participant engagement and authentic expression. His approach is characterized by creating a welcoming environment where all voices can be heard. By beginning each session with light conversation and rapport-building introductions, he sets a tone of comfort that eases participants into sharing their perspectives. Della Volpe employs open-ended questions strategically, designed to invite thoughtful responses rather than simple affirmations or denials. This technique not only stimulates deeper reflections but also opens pathways to organic discussions that explore a variety of themes and personal stories, especially on sensitive subjects. Throughout these conversations, Della Volpe exhibits exceptional active listening skills, frequently paraphrasing and validating participants' contributions, which enhances their sense of worth and fosters an atmosphere of mutual respect. By recognizing the value of diverse viewpoints, he carefully balances guiding the dialogue and allowing natural conversations to emerge, demonstrating adaptability to participants' flow of ideas while maintaining focus on broader topics. His structured yet fluid approach leads to enriched discussions, where he encourages quieter participants to voice their thoughts while also managing the dynamics of more vocal members to ensure equitable participation across the board. Della Volpe's emphasis on creating a safe space for dialogue—where no opinion is deemed 'right' or 'wrong'—promotes openness and vulnerability among participants. By stating guidelines for respectful communication and encouraging back-and-forth exchanges, he cultivates a climate of trust that allows participants to delve into potentially controversial topics without fear of judgment. His adept management of differing perspectives transforms potential conflicts into opportunities for productive dialogue, enriching the conversation and enhancing the participants' learning experience. Moreover, he skillfully summarizes key points and themes, synthesizing insights shared during the discussions. This practice not only reinforces participants' contributions but also helps them see the bigger picture formed from their shared experiences. Della Volpe's interactive style integrates moments of personal sharing, not only to model openness but to deepen connections among participants, enhancing the overall engagement in the conversation. Ultimately, John Della Volpe's interview style is an exemplary model of facilitation that champions inclusivity and equity. It fosters a community atmosphere where individuals feel empowered to express their thoughts and experiences, contributing to a richer understanding of the issues at hand. By valuing each participant's voice and encouraging critical thinking, Della Volpe ensures that conversations delve beyond surface level, leading to nuanced insights that resonate meaningfully with the group. His respectful, engaging, and adaptive approach positions him as not merely an interviewer but a facilitator of profound understanding and collaboration among the participants.",
    "examples": "1. \\"There's no right answer. There's no wrong answer.\\" 2. \\"I basically just wanna ask how things are going. Right?\\" 3. \\"You guys know each other, but why don't you get to know each other?\\" 4. \\"This thing works well when we have as many opinions on the table as possible.\\" 5. \\"If you feel a way that might be different than someone else, there are a lot of other people who probably feel the same way.\\" 6. \\"I just wanna know what's a good day and what's a stressful day.\\" 7. \\"Do you have any questions for me?\\" 8. \\"Why would you not want to share your opinion?\\" 9. \\"What was the topic? It was like a food thing.\\" 10. \\"How about health care? What should be prioritized?\\" 11. \\"What do you think about student loans and debt relief?\\" 12. \\"Do you think politicians understand your stressors?\\" 13. \\"I try to explain that, basically, I'm a professional listener.\\" 14. \\"Unlike so many other parts of your life, I'm not looking for any answer.\\" 15. \\"I think this is a good size.\\" 16. \\"Are there any other issues that you care about that you want to see addressed up here?\\" 17. \\"Did the best we could with that.\\" 18. \\"Does anyone feel understood?\\" 19. \\"What's the best thing in your life right now?\\" 20. \\"We want to be able to hear from all of you.\\" 21. \\"If you're somebody who doesn't talk very much, I may be calling on you.\\" 22. \\"Just be respectful of other folks in the room.\\" 23. \\"I want to hear as many different perspectives as I can.\\" 24. \\"If you could just introduce yourself and tell me the best thing happening in your life right now.\\" 25. \\"I want to emphasize that I'm just here to listen.\\" 26. \\"It's about the connection of your generation.\\" 27. \\"I want to hear more about your thoughts on this issue.\\" 28. \\"What are some of the biggest stressors in your life?\\" 29. \\"Let's keep the conversation going, I appreciate you all.\\" 30. \\"What's keeping you up at night?\\" 31. \\"Talk to me. What's stressing you out?\\" 32. \\"How do you think you might have to do differently because of that?\\" 33. \\"What do some others think about that?\\" 34. \\"It's really interesting to kinda compare and contrast, and, that's what I do.\\" 35. \\"It's pretty good that we're not doing this on Zoom.\\" 36. \\"If you haven't thought about it before, that's also helpful.\\" 37. \\"I appreciate this so much. This is, like, so important. I wanna make sure I hear everybody.\\" 38. \\"What are the kinds of issues that cause stress or keep you up at night?\\" 39. \\"Does it matter? Does it make a difference?\\" 40. \\"I want to sort of talk about some of the positives, negatives, and that.\\" 41. \\"I really just wanna have a conversation about what life is like as young people in Philadelphia right now.\\" 42. \\"This is a space where I think that, like, they bring in new cars.\\" 43. \\"Let's start with introductions.\\" 44. \\"It's important that we hear it.\\" 45. \\"For those of you who said a hundred percent will vote, like, why are you gonna vote?\\" 46. \\"So, thank you, everyone, for being here.\\" 47. \\"If you have a different take, a different perspective, like, that's why you're here.\\" 48. \\"If you're somebody who doesn't talk a lot, I may ask you to speak up a little.\\" 49. \\"We want to capture what was said within each conversation.\\" 50. \\"What do you think the government can do?\\""
}
Here are the INTERVIEW TOPICS and related QUESTIONS you want answered.
The main questions are mentioned in the tag "<question>" and if there's a valid follow up required, the follow up questions are present in the tag "<followup>" present in its respective <question> tag.
To start the interview, ask the user to tell me the 6 digit pin number. Then call 'get_pin'. Then ask the following questions.
To begin the interview process, please say:
"Before we start, I'll need you to verify your participation by telling me the 6-digit PIN you received in your invitation. This helps us track responses while keeping your identity confidential."
Then call 'get_pin'. Then ask the following questions.    
`;

const DEFAULT_CLOSING_NOTE = `After asking these questions, thank them for participating and ask if they have any other questions.  If they say no, say "Ok, thanks for chatting today. You may now hang up the phone."`;

// Helper function to extract duration from questions XML
const extractDurationFromQuestions = (questionsText) => {
  if (!questionsText) return null;
  const match = questionsText.match(/<break\s+time="([\d.]+)s"/);
  return match ? parseFloat(match[1]) : null;
};

// Helper function to calculate estimated duration (in minutes)
const calculateEstimatedDuration = (questionsText) => {
  if (!questionsText || !questionsText.trim()) return 1; // Default to 1 min if no questions

  // Count only main questions (not followups)
  const questionMatches = questionsText.match(/<question[\s>]/g);
  const questionCount = questionMatches ? questionMatches.length : 0;

  // Calculate: questions * 1 minute, capped between 1-60
  const calculatedDuration = questionCount * 1;
  return Math.min(60, Math.max(1, calculatedDuration));
};

const CreatePollingAgent = (props) => {
  const {
    createPollingAgent,
    createPollingAgentReset,
    createPollingAgentState,
    updatePollingAgent,
    updatePollingAgentReset,
    updatePollingAgentState,
    getPollingAgentById,
    getPollingAgentByIdReset,
    getPollingAgentByIdState,
    getVoices,
    getVoicesReset,
    getVoicesState,
    getSurveyDetail,
    getSurveyDetailReset,
    getSurveyDetailState,
    getSurveys,
    getSurveysReset,
    getSurveysState,
    getPollingAgents,
    getPollingAgentsState,
  } = props;

  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const isEdit = !!id;

  // Permission check - shortcut hook (must be before any conditional returns)
  const { can, canPerform } = usePollingAgentPermissions();
  
  // Field-level permission checks
  const canEditLLM = canPerform('ai-jdv', 'edit-llm');
  const canEditMaxDuration = canPerform('ai-jdv', 'edit-max-duration');
  const canEditCallTone = canPerform('ai-jdv', 'edit-call-tone');
  const canEditClosingNote = canPerform('ai-jdv', 'edit-closing-note');
  const canEditQuestionDuration = canPerform('ai-jdv', 'edit-question-duration');
  const canEditAgentNumber = canPerform('ai-jdv', 'agent-number');

  const searchParams = new URLSearchParams(location.search);
  const surveyId = searchParams.get('survey');
  const copyFromId = searchParams.get('copyFrom');
  const copyFromAgentName = searchParams.get('agentName');
  const createInFolderId = searchParams.get('folderId');
  const createInFolderName = searchParams.get('folderName');

  // Permission checks for new features
  const canLoadFromSurvey = can('load-from-survey');
  const canDuplicate = can('duplicate');
  const canExport = can('export');
  const canImport = can('import');

  const [form] = ThemeForm.useForm();
  const [formData, setFormData] = useState({
    name: '',
    agent_name: '',
    agent_id: '',
    max_duration_seconds: '1800',
    llm: 'gemini-2.5-flash',
    temperature: '0.1',
    language: 'en',
    tts_model: 'eleven_flash_v2',
    turn_timeout: '7',
    first_message: '',
    flow: '',
    prompt_call_tone: DEFAULT_CALL_TONE,
    prompt_questions: '',
    prompt_closing_note: DEFAULT_CLOSING_NOTE,
    voice_id: '',
    phone_numbers: [],
  });

  const [selectedVoice, setSelectedVoice] = useState(null);
  const [voices, setVoices] = useState([]);
  const [filteredVoices, setFilteredVoices] = useState([]);
  const [selectedNationality, setSelectedNationality] = useState('all');
  const [selectedGender, setSelectedGender] = useState('all');
  const [isSurveyModalVisible, setIsSurveyModalVisible] = useState(false);
  const [isVoiceModalVisible, setIsVoiceModalVisible] = useState(false);
  const [tempSelectedVoice, setTempSelectedVoice] = useState(null);
  const [surveys, setSurveys] = useState([]);
  const [filteredSurveys, setFilteredSurveys] = useState([]);
  const [surveySearchText, setSurveySearchText] = useState('');
  const [saveAction, setSaveAction] = useState(null); // 'goBack' or 'createStudy' for connected flow
  const [createdAgentId, setCreatedAgentId] = useState(null);
  const [globalQuestionDuration, setGlobalQuestionDuration] = useState(4.0);
  const [modalTab, setModalTab] = useState('surveys');
  const [agents, setAgents] = useState([]);
  const [filteredAgents, setFilteredAgents] = useState([]);
  const [agentSearchText, setAgentSearchText] = useState('');
  const [copyModalVisible, setCopyModalVisible] = useState(false);
  const [copyAgentName, setCopyAgentName] = useState('');

  const audioRef = useRef(null);
  const [voicePlayingId, setVoicePlayingId] = useState(null);
  const [voiceLoadingId, setVoiceLoadingId] = useState(null);
  const questionBuilderStopRef = useRef(null);

  const getAvatarColor = (name) => {
    const colors = ['#260E52', '#4B1B91', '#5F42DE', '#34B3F1', '#00B8BA', '#48B569', '#E65B5B'];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  // Initialize form with default values
  useEffect(() => {
    if (!isEdit) {
      form.setFieldsValue({
        max_duration_seconds: '1800',
        llm: 'gemini-2.5-flash',
        temperature: '0.1',
        language: 'en',
        tts_model: 'eleven_flash_v2',
        turn_timeout: '7',
        prompt_call_tone: DEFAULT_CALL_TONE,
        prompt_closing_note: DEFAULT_CLOSING_NOTE,
      });
    }
  }, [form, isEdit]);

  // Fetch voices on component mount
  useEffect(() => {
    getVoices();
    return () => {
      getVoicesReset();
    };
  }, [getVoices, getVoicesReset]);

  // Sort voices: John Della Volpe v2 first, then original, then my voices (cloned/professional), then premade
  const sortVoices = (voicesList) => {
    return [...voicesList].sort((a, b) => {
      const getOrder = (v) => {
        if (v.voice_id === 'kVYakPWKGwV1guNlg6lU') return 0; // John Della Volpe v2
        if (v.name?.toLowerCase().includes('john della volpe')) return 1; // John Della Volpe original
        if (v.category === 'cloned' || v.category === 'professional') return 2; // My voices
        return 3; // Default/premade
      };
      return getOrder(a) - getOrder(b);
    });
  };

  // Handle voices response
  useEffect(() => {
    if (getVoicesState.apiState === 'success' && getVoicesState.data) {
      const voicesData = sortVoices(getVoicesState.data.voices || []);
      setVoices(voicesData);
      setFilteredVoices(voicesData);
    } else if (getVoicesState.apiState === 'error') {
      notification.error({
        message: 'Error',
        description: getVoicesState.message || 'Failed to fetch voices',
      });
    }
  }, [getVoicesState]);

  useEffect(() => {
    let filtered = voices;

    if (selectedNationality !== 'all') {
      filtered = filtered.filter(voice => {
        const accent = voice.labels?.accent?.toLowerCase();
        return accent === selectedNationality.toLowerCase();
      });
    }

    if (selectedGender !== 'all') {
      filtered = filtered.filter(voice => {
        const gender = voice.labels?.gender?.toLowerCase();
        return gender === selectedGender.toLowerCase();
      });
    }

    setFilteredVoices(filtered);
  }, [selectedNationality, selectedGender, voices]);

  // Fetch survey details if survey ID is provided (for new agent creation only)
  useEffect(() => {
    if (surveyId && !isEdit) {
      getSurveyDetail(surveyId);
    }
    return () => {
      if (surveyId && !isEdit) {
        getSurveyDetailReset();
      }
    };
  }, [surveyId, isEdit, getSurveyDetail, getSurveyDetailReset]);

  // Handle survey detail response and populate interview dialogues
  useEffect(() => {
    if (getSurveyDetailState.apiState === 'success' && getSurveyDetailState.data) {
      const responseData = getSurveyDetailState.data.data || getSurveyDetailState.data;
      const questions = responseData.questions || [];

      if (questions.length > 0) {
        // Helper to build media XML attributes
        const escXml = (str) => (str || '').replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
        const mediaAttrs = (m) => {
          if (!m || !m.media_type) return '';
          return ` has_media="true" media_type="${m.media_type}" media_id="${m.file_id || ''}" media_file_key="${m.file_key || ''}" media_url="${escXml(m.url || '')}" media_filename="${escXml(m.filename || '')}"`;
        };

        // Convert questions to XML format with followups and media
        const dialogues = questions.map(q => {
          let xml = `<question${mediaAttrs(q.media)}>\n${(q.question_text || '').trim()}\n<break time="${globalQuestionDuration}s"/>\n`;
          // Add followups if present
          if (q.followups && q.followups.length > 0) {
            q.followups.forEach(f => {
              xml += `<followup${mediaAttrs(f.media)}>\n${(f.text || '').trim()}\n</followup>\n<break time="${globalQuestionDuration}s"/>\n`;
            });
          }
          xml += '</question>';
          return xml;
        }).join('\n');

        const totalFollowups = questions.reduce((sum, q) => sum + (q.followups?.length || 0), 0);

        const updatedFormData = {
          ...formData,
          prompt_questions: dialogues,
        };

        setFormData(updatedFormData);
        form.setFieldsValue({
          prompt_questions: dialogues,
        });

        notification.success({
          key: 'survey-questions-loaded',
          message: 'Survey Questions Loaded',
          description: `${questions.length} question${questions.length !== 1 ? 's' : ''}${totalFollowups > 0 ? ` with ${totalFollowups} follow-up${totalFollowups !== 1 ? 's' : ''}` : ''} imported from survey.`,
          duration: 3,
        });
      }

      getSurveyDetailReset();
    } else if (getSurveyDetailState.apiState === 'error') {
      notification.error({
        message: 'Error Loading Survey',
        description: getSurveyDetailState.message || 'Failed to fetch survey details',
      });
      getSurveyDetailReset();
    }
  }, [getSurveyDetailState, form, formData, getSurveyDetailReset, globalQuestionDuration]);

  // Handle surveys response
  useEffect(() => {
    if (getSurveysState.apiState === 'success' && getSurveysState.data) {
      const responseData = getSurveysState.data.data || getSurveysState.data;
      const surveysData = responseData.surveys || responseData || [];
      setSurveys(surveysData);
      setFilteredSurveys(surveysData);
    } else if (getSurveysState.apiState === 'error') {
      notification.error({
        message: 'Error',
        description: getSurveysState.message || 'Failed to fetch surveys',
      });
    }
  }, [getSurveysState]);

  // Filter surveys based on search text
  useEffect(() => {
    if (surveySearchText.trim() === '') {
      setFilteredSurveys(surveys);
    } else {
      const filtered = surveys.filter(survey =>
        survey.title?.toLowerCase().includes(surveySearchText.toLowerCase())
      );
      setFilteredSurveys(filtered);
    }
  }, [surveySearchText, surveys]);

  // Handle polling agents response for modal
  useEffect(() => {
    if (getPollingAgentsState.apiState === 'success' && getPollingAgentsState.data) {
      const responseData = getPollingAgentsState.data.data || getPollingAgentsState.data;
      const agentsData = responseData.polling_agents || responseData || [];
      // Exclude current agent if in edit mode
      const filtered = isEdit ? agentsData.filter(a => String(a._id) !== String(id)) : agentsData;
      setAgents(filtered);
      setFilteredAgents(filtered);
    }
  }, [getPollingAgentsState, isEdit, id]);

  // Filter agents based on search text
  useEffect(() => {
    if (agentSearchText.trim() === '') {
      setFilteredAgents(agents);
    } else {
      const filtered = agents.filter(agent =>
        agent.agent_name?.toLowerCase().includes(agentSearchText.toLowerCase())
      );
      setFilteredAgents(filtered);
    }
  }, [agentSearchText, agents]);

  // Fetch agent data if editing or copying from another agent
  useEffect(() => {
    if (isEdit && id) {
      getPollingAgentById(id);
    } else if (copyFromId && !isEdit) {
      getPollingAgentById(copyFromId);
    }
  }, [id, isEdit, copyFromId, getPollingAgentById]);

  // Handle agent data response for edit
  useEffect(() => {
    if (getPollingAgentByIdState.apiState === 'success' && getPollingAgentByIdState.data && isEdit) {
      const agent = getPollingAgentByIdState.data;
      const updatedData = {
        name: agent.name || '',
        agent_name: agent.agent_name || '',
        agent_id: agent.agent_id || '',
        max_duration_seconds: agent.max_duration_seconds ? agent.max_duration_seconds.toString() : '1800',
        llm: agent.llm || 'gemini-2.5-flash',
        temperature: agent.temperature !== undefined ? agent.temperature.toString() : '0.1',
        language: agent.language || 'en',
        tts_model: agent.tts_model || 'eleven_flash_v2',
        turn_timeout: agent.turn_timeout !== undefined ? agent.turn_timeout.toString() : '7',
        first_message: agent.first_message || '',
        flow: agent.flow || '',
        prompt_call_tone: agent.prompt_call_tone || '',
        prompt_questions: agent.prompt_questions || '',
        prompt_closing_note: agent.prompt_closing_note || '',
        voice_id: agent.voice_id || '',
        phone_numbers: agent.phone_numbers || [],
      };
      const extractedDuration = extractDurationFromQuestions(agent.prompt_questions);
      if (extractedDuration) {
        setGlobalQuestionDuration(extractedDuration);
      } else {
        setGlobalQuestionDuration(4.0);
      }

      setFormData(updatedData);
      form.setFieldsValue({
        ...updatedData,
        phone_number: agent.phone_numbers?.[0]?.number || '',
      });
      getPollingAgentByIdReset();
    } else if (getPollingAgentByIdState.apiState === 'success' && getPollingAgentByIdState.data && copyFromId && !isEdit) {
      // Handle Copy & Create New - populate all fields except agent_name
      const agent = getPollingAgentByIdState.data;
      const updatedData = {
        name: copyFromAgentName || '',
        agent_name: copyFromAgentName || '',
        agent_id: '',
        max_duration_seconds: agent.max_duration_seconds ? agent.max_duration_seconds.toString() : '1800',
        llm: agent.llm || 'gemini-2.5-flash',
        temperature: agent.temperature !== undefined ? agent.temperature.toString() : '0.1',
        language: agent.language || 'en',
        tts_model: agent.tts_model || 'eleven_flash_v2',
        turn_timeout: agent.turn_timeout !== undefined ? agent.turn_timeout.toString() : '7',
        first_message: agent.first_message || '',
        flow: '',
        prompt_call_tone: agent.prompt_call_tone || '',
        prompt_questions: agent.prompt_questions || '',
        prompt_closing_note: agent.prompt_closing_note || '',
        voice_id: agent.voice_id || '',
        phone_numbers: [],
      };
      const extractedDuration = extractDurationFromQuestions(agent.prompt_questions);
      if (extractedDuration) {
        setGlobalQuestionDuration(extractedDuration);
      } else {
        setGlobalQuestionDuration(4.0);
      }

      setFormData(updatedData);
      form.setFieldsValue({
        ...updatedData,
        phone_number: '',
      });
      getPollingAgentByIdReset();
      notification.success({
        message: 'Agent Copied',
        description: copyFromAgentName
          ? 'All fields have been copied.'
          : 'All fields have been copied. Please enter a new agent name.',
        duration: 3,
      });
    } else if (getPollingAgentByIdState.apiState === 'error') {
      notification.error({
        message: 'Error',
        description: getPollingAgentByIdState.message || 'Failed to fetch AI JDV agent',
      });
      getPollingAgentByIdReset();
      if (isEdit) {
        navigate('/polling-agents/');
      }
    }
  }, [getPollingAgentByIdState, isEdit, copyFromId, getPollingAgentByIdReset, navigate, form]);

  // Handle voice selection - runs when voices are loaded and formData has voice_id
  useEffect(() => {
    if ((isEdit || copyFromId) && formData.voice_id && voices.length > 0) {
      const voiceIndex = voices.findIndex(v => v.voice_id === formData.voice_id);
      if (voiceIndex !== -1) {
        setSelectedVoice(voiceIndex);
      }
    }
  }, [voices, formData.voice_id, isEdit, copyFromId]);

  // Handle create response
  useEffect(() => {
    if (createPollingAgentState.apiState === 'success') {
      const agentData = createPollingAgentState.data;
      const newAgentId = agentData?._id || agentData?.id || agentData?.agent?._id;
      if (newAgentId) {
        setCreatedAgentId(newAgentId);

        // If created from inside a folder, add the new agent to that folder (direct API call, no Redux state)
        if (createInFolderId) {
          axios.post(
            `${config.api.base_url}/folders/${createInFolderId}/add-item`,
            { itemId: newAgentId },
            { headers: { Authorization: 'Bearer ' + getUserToken() } }
          ).catch(() => {});
        }

        // If action was specified, navigate directly
        if (!isEdit && saveAction) {
          if (saveAction === 'goBack') {
            // Navigate to listing page (back into folder if came from one)
            if (createInFolderId) {
              navigate(`/polling-agents/?folderId=${createInFolderId}&folderName=${encodeURIComponent(createInFolderName || '')}`);
            } else {
              navigate('/polling-agents/');
            }
          } else if (saveAction === 'createStudy') {
            // Navigate to study creation page
            let url = '/campaigns/create-campaign';
            const params = new URLSearchParams();
            params.append('agent', newAgentId);
            if (surveyId) {
              params.append('survey', surveyId);
            }
            url += '?' + params.toString();
            navigate(url);
          }
          setSaveAction(null);
          createPollingAgentReset();
          return;
        }
      }
      // If no action specified, just reset - buttons will handle navigation
      createPollingAgentReset();
    } else if (createPollingAgentState.apiState === 'error') {
      notification.error({
        message: 'Error',
        description: createPollingAgentState.message || 'Failed to create AI JDV agent',
      });
      createPollingAgentReset();
    }
  }, [createPollingAgentState, createPollingAgentReset, surveyId, isEdit, saveAction, navigate]);

  // Handle update response
  useEffect(() => {
    if (updatePollingAgentState.apiState === 'success') {
      notification.success({
        message: 'Success',
        description: updatePollingAgentState.message || 'AI JDV agent updated successfully',
      });
      updatePollingAgentReset();
      navigate('/polling-agents/');
    } else if (updatePollingAgentState.apiState === 'error') {
      notification.error({
        message: 'Error',
        description: updatePollingAgentState.message || 'Failed to update AI JDV agent',
      });
      updatePollingAgentReset();
    }
  }, [updatePollingAgentState, updatePollingAgentReset, navigate]);

  const validateAgentDetails = async () => {
    try {
      const fieldsToValidate = ['agent_name', 'first_message', 'prompt_questions'];
      
      // Only validate fields user has permission to edit
      if (canEditLLM) fieldsToValidate.push('llm');
      if (canEditMaxDuration) fieldsToValidate.push('max_duration_seconds');
      if (canEditCallTone) fieldsToValidate.push('prompt_call_tone');
      if (canEditClosingNote) fieldsToValidate.push('prompt_closing_note');
      
      await form.validateFields(fieldsToValidate);
      return true;
    } catch (error) {
      return false;
    }
  };

  const handleOpenVoiceModal = () => {
    // Set temp selection to current selection (or null if none)
    setTempSelectedVoice(selectedVoice !== null && selectedVoice !== undefined ? selectedVoice : null);
    setIsVoiceModalVisible(true);
  };

  const handleCloseVoiceModal = () => {
    setIsVoiceModalVisible(false);
    setTempSelectedVoice(null);
  };

  const handleSaveVoiceSelection = () => {
    if (tempSelectedVoice !== null && tempSelectedVoice !== undefined) {
      setSelectedVoice(tempSelectedVoice);
    }
    setIsVoiceModalVisible(false);
    setTempSelectedVoice(null);
  };

  const composePrompt = (callTone, questions, closingNote) => {
    let composedPrompt = '';

    if (callTone) {
      composedPrompt += callTone.trim();
    }

    if (questions) {
      if (composedPrompt) {
        composedPrompt += '\n\n';
      }

      // Check if any questions have media attached
      const hasMediaQuestions = questions.includes('has_media="true"');

      composedPrompt += `## Interview Questions
To begin the interview process, say:
"Before we start, I'll need you to verify your participation by telling me the 6-digit PIN you received in your invitation. This helps us track responses while keeping your identity confidential."

After they provide the PIN, call the \`verify_pin\` tool. Once verified, proceed with the following questions:
`;
      if (hasMediaQuestions) {
        composedPrompt += `
## Media Questions — FOLLOW THESE RULES EXACTLY
Some questions have multimedia attachments marked with has_media="true".

### SEQUENCE (do not deviate):
1. When you reach a question with has_media="true", say briefly: "I'm sending some media to your screen. Please go through it carefully and when you're done, press the I'm ready to answer button. Your mic will be muted until then."
2. IMMEDIATELY after saying that, call the \`display_media\` tool with the media_id. Do NOT add anything else before calling it.
3. The tool will block until the participant clicks the button. Do not speak or continue while waiting.

### AFTER PARTICIPANT CLICKS THE BUTTON:
- Now read the question text aloud clearly and completely.
- If the question has follow-ups, ask them after the participant answers the main question.
- Do NOT skip the question — the media was shown first so they have context, now ask the actual question.

### WHEN PARTICIPANT ANSWERS:
- You CANNOT see the media. NEVER comment on whether their answer is correct or incorrect.
- NEVER say: "good eye", "that's right", "interesting observation", or anything similar.
- Acknowledge neutrally: "Thank you" or "Got it" — then move on.

### IF PARTICIPANT ASKS TO SEE MEDIA AGAIN OR REPEAT THE QUESTION:
- Say "Sure, let me show you that again."
- Call \`display_media\` again with the SAME media_id.
- After they click the button again, re-read the question text aloud.

### IF THE TOOL RETURNS AN ERROR:
- Say "Sorry, the media isn't loading. Let me continue." and move on.

`;
      }
      composedPrompt += `<speak>
${questions.trim()}
</speak>`;
    }

    if (closingNote) {
      if (composedPrompt) {
        composedPrompt += '\n';
      }
      composedPrompt += closingNote.trim();
    }

    return composedPrompt;
  };

  const handleLoadDefaultCallTone = () => {
    const updatedFormData = {
      ...formData,
      prompt_call_tone: DEFAULT_CALL_TONE,
    };
    setFormData(updatedFormData);
    form.setFieldsValue({
      prompt_call_tone: DEFAULT_CALL_TONE,
    });
    notification.success({
      message: 'Default Call Tone Loaded',
      description: 'Default call tone has been loaded successfully.',
      duration: 2,
    });
  };

  const handleLoadDefaultClosingNote = () => {
    const updatedFormData = {
      ...formData,
      prompt_closing_note: DEFAULT_CLOSING_NOTE,
    };
    setFormData(updatedFormData);
    form.setFieldsValue({
      prompt_closing_note: DEFAULT_CLOSING_NOTE,
    });
    notification.success({
      message: 'Default Closing Note Loaded',
      description: 'Default closing note has been loaded successfully.',
      duration: 2,
    });
  };

  const handleOpenSurveyModal = () => {
    getSurveys({ limit: 999 });
    setIsSurveyModalVisible(true);
    setModalTab('surveys');
  };

  const handleCloseSurveyModal = () => {
    setIsSurveyModalVisible(false);
    setSurveySearchText('');
    setAgentSearchText('');
    setModalTab('surveys');
  };

  const handleModalTabChange = (key) => {
    setModalTab(key);
    setSurveySearchText('');
    setAgentSearchText('');
    if (key === 'agents' && agents.length === 0) {
      getPollingAgents({ limit: 999 });
    }
  };

  const handleSelectSurvey = async (surveyRecord) => {
    getSurveyDetail(surveyRecord._id);
    setIsSurveyModalVisible(false);
    setSurveySearchText('');
    setAgentSearchText('');
    setModalTab('surveys');
  };

  const handleSelectAgent = (agentRecord) => {
    const questionsXml = agentRecord.prompt_questions || '';
    const questionCount = (questionsXml.match(/<question[\s>]/g) || []).length;

    const updatedFormData = {
      ...formData,
      prompt_questions: questionsXml,
    };

    setFormData(updatedFormData);
    form.setFieldsValue({
      prompt_questions: questionsXml,
    });

    // Extract duration from the loaded agent's questions
    const extractedDuration = extractDurationFromQuestions(questionsXml);
    if (extractedDuration) {
      setGlobalQuestionDuration(extractedDuration);
    }

    setIsSurveyModalVisible(false);
    setAgentSearchText('');
    setSurveySearchText('');
    setModalTab('surveys');

    notification.success({
      key: 'agent-questions-loaded',
      message: 'Questions Loaded',
      description: `${questionCount} question${questionCount !== 1 ? 's' : ''} loaded from agent.`,
      duration: 3,
    });
  };

  const handleCopyAndCreateNew = () => {
    setCopyAgentName('');
    setCopyModalVisible(true);
  };

  const handleCopyConfirm = () => {
    if (!copyAgentName.trim()) return;
    setCopyModalVisible(false);
    navigate(`/polling-agents/polling-agent/create-polling-agent?copyFrom=${id}&agentName=${encodeURIComponent(copyAgentName.trim())}`);
  };

  // Export/Import handlers
  const importFileRef = useRef(null);
  const [importModalVisible, setImportModalVisible] = useState(false);
  const [pendingImport, setPendingImport] = useState(null);

  const handleExportQuestions = (format) => {
    const questionsXml = form.getFieldValue('prompt_questions') || formData.prompt_questions || '';
    if (!questionsXml || !/<question[\s>]/.test(questionsXml)) {
      notification.warning({ message: 'No questions to export' });
      return;
    }
    // Helper to parse media attributes from XML tag
    const getAttr = (attrStr, name) => {
      const m = (attrStr || '').match(new RegExp(`${name}="([^"]*)"`));
      return m ? m[1] : '';
    };
    const unesc = (str) => str.replace(/&amp;/g, '&').replace(/&quot;/g, '"').replace(/&lt;/g, '<').replace(/&gt;/g, '>');
    const extractMedia = (attrStr) => {
      if (!attrStr || !attrStr.includes('has_media="true"')) return null;
      return {
        media_type: getAttr(attrStr, 'media_type'),
        file_key: getAttr(attrStr, 'media_file_key'),
        url: unesc(getAttr(attrStr, 'media_url')),
        filename: unesc(getAttr(attrStr, 'media_filename')),
      };
    };

    // Parse XML to objects with media attributes
    const parsed = [];
    const questionRegex = /<question([^>]*)>([\s\S]*?)<\/question>/g;
    let match;
    while ((match = questionRegex.exec(questionsXml)) !== null) {
      const attrs = match[1];
      const content = match[2];
      let qText = '';
      for (const line of content.split('\n')) {
        const t = line.trim();
        if (t.startsWith('<break') || t.startsWith('<followup')) break;
        if (t && !t.startsWith('<')) qText += (qText ? ' ' : '') + t;
      }
      const qMedia = extractMedia(attrs);
      const followups = [];
      const fRegex = /<followup([^>]*)>([\s\S]*?)<\/followup>/g;
      let fMatch;
      while ((fMatch = fRegex.exec(content)) !== null) {
        const fAttrs = fMatch[1];
        const fText = fMatch[2].trim().replace(/\s+/g, ' ');
        const fMedia = extractMedia(fAttrs);
        if (fText) followups.push({ text: fText, ...(fMedia ? { media: fMedia } : {}) });
      }
      if (qText.trim()) parsed.push({ text: qText.trim(), followups, ...(qMedia ? { media: qMedia } : {}) });
    }
    const normalized = normalizeQuestions(parsed, 'agent');
    const name = formData.name || 'agent';
    if (format === 'excel') downloadAsExcel(normalized, name);
    else downloadAsText(normalized, name);
  };

  const exportMenuItems = [
    { key: 'excel', label: 'Excel (.xls)', onClick: () => handleExportQuestions('excel') },
    { key: 'text', label: 'Text (.txt)', onClick: () => handleExportQuestions('text') },
  ];

  const handleImportFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    const { questions, error } = await parseImportFile(file);
    if (error) {
      notification.error({ message: error });
      return;
    }
    setPendingImport({ questions, fileName: file.name });
    setImportModalVisible(true);
  };

  const handleImportConfirm = () => {
    if (!pendingImport) return;
    const { questions } = pendingImport;
    // Helper to build media XML attrs from import metadata
    const escXml = (str) => (str || '').replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const importMediaAttrs = (m) => {
      if (!m || !m.media_type) return '';
      return ` has_media="true" media_type="${m.media_type}" media_id="" media_file_key="${m.file_key || ''}" media_url="" media_filename="${escXml(m.filename || '')}"`;
    };
    // Build XML from parsed questions with media
    const xml = questions.map(q => {
      let c = `<question${importMediaAttrs(q.media)}>\n${q.questionText}\n<break time="${globalQuestionDuration}s"/>\n`;
      (q.followups || []).forEach(f => {
        const fText = typeof f === 'string' ? f : (f.text || '');
        const fMedia = typeof f === 'object' ? f.media : null;
        c += `<followup${importMediaAttrs(fMedia)}>\n${fText}\n</followup>\n<break time="${globalQuestionDuration}s"/>\n`;
      });
      return c + '</question>';
    }).join('\n');

    const updatedFormData = { ...formData, prompt_questions: xml };
    setFormData(updatedFormData);
    form.setFieldsValue({ prompt_questions: xml });

    setImportModalVisible(false);
    notification.success({
      message: 'Questions Imported',
      description: `${questions.length} question${questions.length !== 1 ? 's' : ''} imported from file.`,
      duration: 3,
    });
    setPendingImport(null);
  };


  const handleSaveAndGoBack = async () => {
    setSaveAction('goBack');
    await handleSaveInternal();
  };

  const handleSaveAndCreateStudy = async () => {
    setSaveAction('createStudy');
    await handleSaveInternal();
  };

  const handleSave = async () => {
    setSaveAction(null);
    await handleSaveInternal();
  };

  const handleSaveInternal = async () => {
    const currentValues = form.getFieldsValue();
    const allValues = {
      ...formData,
      ...currentValues,
    };

    if (!allValues.agent_name || !allValues.agent_name.trim()) {
      notification.error({
        message: 'Validation Error',
        description: 'Please enter agent name',
      });
      return;
    }

    if (!allValues.first_message || !allValues.first_message.trim()) {
      notification.error({
        message: 'Validation Error',
        description: 'Please enter first message',
      });
      return;
    }

    if (!allValues.prompt_questions || !allValues.prompt_questions.trim()) {
      notification.error({
        message: 'Validation Error',
        description: 'Please enter interview questions',
      });
      return;
    }

    // Validate that there is at least one question with non-empty text
    const questionMatches = allValues.prompt_questions.match(/<question[^>]*>([\s\S]*?)<\/question>/g);
    if (!questionMatches || questionMatches.length === 0) {
      notification.error({
        message: 'Validation Error',
        description: 'Please add at least one question',
      });
      return;
    }

    // Validate that no question is empty
    for (const questionBlock of questionMatches) {
      // Extract question text (before first break or followup tag)
      const questionTextMatch = questionBlock.match(/<question[^>]*>\s*(.*?)\s*<break/s);
      const questionText = questionTextMatch ? questionTextMatch[1].trim() : '';

      if (!questionText) {
        notification.error({
          message: 'Validation Error',
          description: 'Questions cannot be empty. Please add text to all questions',
        });
        return;
      }

      // Validate followups in this question
      const followupMatches = questionBlock.match(/<followup[^>]*>([\s\S]*?)<\/followup>/g);
      if (followupMatches && followupMatches.length > 0) {
        for (const followupBlock of followupMatches) {
          // Extract followup text
          const followupTextMatch = followupBlock.match(/<followup[^>]*>\s*(.*?)\s*<\/followup>/s);
          const followupText = followupTextMatch ? followupTextMatch[1].trim() : '';

          if (!followupText) {
            notification.error({
              message: 'Validation Error',
              description: 'Follow-ups cannot be empty. Please add text to all follow-ups or remove them',
            });
            return;
          }
        }
      }
    }

    if (selectedVoice === null || selectedVoice === undefined || typeof selectedVoice !== 'number' || selectedVoice < 0) {
      notification.error({
        message: 'Validation Error',
        description: 'Please select a voice for the agent',
      });
      return;
    }

    const selectedVoiceData = voices[selectedVoice];

    if (!selectedVoiceData || !selectedVoiceData.voice_id) {
      notification.error({
        message: 'Validation Error',
        description: 'Selected voice is invalid. Please select a voice again',
      });
      return;
    }

    // Validate LLM only if user has permission to edit it
    if (canEditLLM && !allValues.llm) {
      notification.error({
        message: 'Validation Error',
        description: 'Please select an LLM model',
      });
      return;
    }

    // Validate Max Duration only if user has permission to edit it
    if (canEditMaxDuration && !allValues.max_duration_seconds) {
      notification.error({
        message: 'Validation Error',
        description: 'Please select max duration seconds',
      });
      return;
    }

    // Use existing value or default if user doesn't have permission
    const duration = canEditMaxDuration 
      ? parseInt(allValues.max_duration_seconds) 
      : (formData.max_duration_seconds ? parseInt(formData.max_duration_seconds) : 1800);
    
    if (canEditMaxDuration && (isNaN(duration) || duration < 10 || duration > 3600)) {
      notification.error({
        message: 'Validation Error',
        description: 'Max duration must be between 10 and 3600 seconds',
      });
      return;
    }

    // Validate Call Tone only if user has permission to edit it
    if (canEditCallTone && (!allValues.prompt_call_tone || !allValues.prompt_call_tone.trim())) {
      notification.error({
        message: 'Validation Error',
        description: 'Please enter call tone instructions',
      });
      return;
    }

    // Validate Closing Note only if user has permission to edit it
    if (canEditClosingNote && (!allValues.prompt_closing_note || !allValues.prompt_closing_note.trim())) {
      notification.error({
        message: 'Validation Error',
        description: 'Please enter closing note',
      });
      return;
    }

    // Use existing values if user doesn't have permission to edit
    const callTone = canEditCallTone 
      ? allValues.prompt_call_tone 
      : (formData.prompt_call_tone || DEFAULT_CALL_TONE);
    const closingNote = canEditClosingNote 
      ? allValues.prompt_closing_note 
      : (formData.prompt_closing_note || DEFAULT_CLOSING_NOTE);

    const composedFlow = composePrompt(
      callTone,
      allValues.prompt_questions,
      closingNote
    );

    // Calculate estimated duration based on questions
    const estimatedDuration = calculateEstimatedDuration(allValues.prompt_questions);

    const submitData = {
      name: allValues.name || allValues.agent_name,
      agent_name: allValues.agent_name,
      max_duration_seconds: duration,
      llm: canEditLLM ? (allValues.llm || 'gemini-2.5-flash') : (formData.llm || 'gemini-2.5-flash'),
      temperature: allValues.temperature ? parseFloat(allValues.temperature) : 0.1,
      language: allValues.language || 'en',
      tts_model: allValues.tts_model || 'eleven_flash_v2',
      turn_timeout: allValues.turn_timeout ? parseInt(allValues.turn_timeout) : 7,
      first_message: allValues.first_message,
      flow: composedFlow,
      prompt_call_tone: callTone,
      prompt_questions: allValues.prompt_questions,
      prompt_closing_note: closingNote,
      estimated_duration: estimatedDuration,
      voice_id: selectedVoiceData?.voice_id || '',
      phone_numbers: formData.phone_numbers || [],
      poll_status: 'ready',
    };

    if (isEdit) {
      updatePollingAgent({ id, ...submitData });
    } else {
      createPollingAgent(submitData);
    }
  };

  const handleVoiceSelect = (index) => {
    setSelectedVoice(index);
  };

  const handleVoiceSelectInModal = (index) => {
    setTempSelectedVoice(index);
  };

  // Get voices to display (3 cards - selected voice first, then 2 others)
  const getDisplayVoices = () => {
    if (voices.length === 0) return [];
    
    if (selectedVoice !== null && selectedVoice !== undefined && selectedVoice >= 0 && selectedVoice < voices.length) {
      // Selected voice first, then 2 others (excluding selected)
      const otherVoices = voices.filter((_, index) => index !== selectedVoice).slice(0, 2);
      return [voices[selectedVoice], ...otherVoices];
    }
    
    // If no selection, show first 3
    return voices.slice(0, 3);
  };

  const handleVoicePreview = (voice) => {
    // If same voice is playing, stop it
    if (voicePlayingId === voice.voice_id) {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      setVoicePlayingId(null);
      return;
    }

    // Stop previous audio if playing
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setVoicePlayingId(null);

    // Stop QuestionBuilder audio
    if (questionBuilderStopRef.current) {
      questionBuilderStopRef.current();
    }

    if (voice.preview_url) {
      setVoiceLoadingId(voice.voice_id);
      const audio = new Audio(voice.preview_url);
      audioRef.current = audio;

      audio.oncanplaythrough = () => {
        setVoiceLoadingId(null);
      };

      audio.onended = () => {
        setVoicePlayingId(null);
        audioRef.current = null;
      };

      audio.onerror = () => {
        setVoicePlayingId(null);
        setVoiceLoadingId(null);
        audioRef.current = null;
        notification.warning({
          message: 'Preview Unavailable',
          description: 'Voice preview could not be played',
        });
      };

      audio.play().then(() => {
        setVoiceLoadingId(null);
        setVoicePlayingId(voice.voice_id);
      }).catch(() => {
        setVoiceLoadingId(null);
        notification.warning({
          message: 'Preview Unavailable',
          description: 'Voice preview could not be played',
        });
      });
    } else {
      notification.info({
        message: 'No Preview',
        description: 'This voice does not have a preview available',
      });
    }
  };

  // Called by QuestionBuilder when it starts playing, so we stop voice preview
  const handleQuestionPlayStart = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setVoicePlayingId(null);
    setVoiceLoadingId(null);
  };


  // Agent Details Tab Content
  const renderAgentDetailsTab = () => (
    <ThemeForm form={form} size="large" layout="vertical">
      {/* Top row with Agent Name, LLM, Max Duration */}
      <AIJDVFormRow gutter={[24, 24]}>
        <Col xs={24} sm={24} md={canEditLLM && canEditMaxDuration ? 8 : canEditLLM || canEditMaxDuration ? 12 : 24}>
          <AIJDVFieldLabel>
            Agent Name
            <Tooltip title="Please provide the name of the agent">
              <InfoCircleOutlined className="info-icon" />
            </Tooltip>
          </AIJDVFieldLabel>
          <AIJDVNoMarginFormItem>
            <ThemeForm.Item
              size="large"
              name="agent_name"
              rules={[{ required: true, message: 'Please enter agent name' }]}
            >
              <ThemeInput size="large" placeholder="Enter agent name" />
            </ThemeForm.Item>
          </AIJDVNoMarginFormItem>
        </Col>
        {canEditLLM && (
          <Col xs={24} sm={24} md={canEditMaxDuration ? 8 : 12}>
            <AIJDVFieldLabel>
              LLM
              <Tooltip title="Prioritizing speed or depth to align with your assistant's responsibilities">
                <InfoCircleOutlined className="info-icon" />
              </Tooltip>
            </AIJDVFieldLabel>
            <AIJDVNoMarginFormItem>
              <ThemeForm.Item
                name="llm"
                rules={canEditLLM ? [{ required: true, message: 'Please select LLM' }] : []}
              >
                <AIJDVSelect
                  placeholder="Select LLM"
                  size="large"
                  options={[
                    { label: "Gemini 2.5 Flash (Default)", value: "gemini-2.5-flash" },
                    { label: "Gemini 2.0 Flash", value: "gemini-2.0-flash" },
                    { label: "Gemini 1.5 Pro", value: "gemini-1.5-pro" },
                    { label: "Gemini 1.5 Flash", value: "gemini-1.5-flash" },
                    { label: "GPT-4o Mini", value: "gpt-4o-mini" },
                    { label: "GPT-4o", value: "gpt-4o" },
                    { label: "GPT-4 Turbo", value: "gpt-4-turbo" },
                    { label: "Claude Sonnet 4", value: "claude-sonnet-4" },
                    { label: "Claude 3.5 Sonnet", value: "claude-3-5-sonnet" },
                    { label: "Claude 3 Haiku", value: "claude-3-haiku" },
                  ]}
                />
              </ThemeForm.Item>
            </AIJDVNoMarginFormItem>
          </Col>
        )}
        {canEditMaxDuration && (
          <Col xs={24} sm={24} md={canEditLLM ? 8 : 12}>
            <AIJDVFieldLabel>
              Max Duration Seconds
              <Tooltip title="The duration must be between 10 and 3600 seconds">
                <InfoCircleOutlined className="info-icon" />
              </Tooltip>
            </AIJDVFieldLabel>
            <AIJDVNoMarginFormItem>
              <ThemeForm.Item
                name="max_duration_seconds"
                rules={canEditMaxDuration ? [
                  { required: true, message: 'Please select max duration' },
                  {
                    validator: (_, value) => {
                      if (!value || (value >= 10 && value <= 3600)) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('Duration must be between 10 and 3600 seconds'));
                    },
                  },
                ] : []}
              >
                <AIJDVSelect
                  placeholder="Select Duration"
                  size="large"
                  options={[
                    { label: "3600", value: "3600" },
                    { label: "1800", value: "1800" },
                    { label: "1200", value: "1200" },
                    { label: "600", value: "600" },
                  ]}
                />
              </ThemeForm.Item>
            </AIJDVNoMarginFormItem>
          </Col>
        )}
      </AIJDVFormRow>

      {/* Phone Number Row */}
      {canEditAgentNumber && (
        <AIJDVFormRow gutter={[24, 24]}>
          <Col xs={24} sm={24} md={8}>
            <AIJDVFieldLabel>
              Agent Phone Number
              <Tooltip title="Phone number for dial-in calls. This number will be displayed on the survey landing page.">
                <InfoCircleOutlined className="info-icon" />
              </Tooltip>
            </AIJDVFieldLabel>
            <AIJDVNoMarginFormItem>
              <ThemeForm.Item
                name="phone_number"
              >
                <ThemeInput
                  size="large"
                  placeholder="+1-XXX-XXX-XXXX"
                  onChange={(e) => {
                    const value = e.target.value;
                    // Sync with formData.phone_numbers for submission
                    if (value.trim()) {
                      setFormData(prev => ({
                        ...prev,
                        phone_numbers: [{ label: 'Dial in', number: value }]
                      }));
                    } else {
                      setFormData(prev => ({
                        ...prev,
                        phone_numbers: []
                      }));
                    }
                  }}
                />
              </ThemeForm.Item>
            </AIJDVNoMarginFormItem>
          </Col>
        </AIJDVFormRow>
      )}

      {/* Study Questionnaire */}
      <AIJDVFormSection>
        <AIJDVFormRowHeader justify="space-between" align="middle" className="pb-3">
          <Col>
            <AIJDVFieldLabel>
              Study Questionnaire
              <Tooltip title="Build the conversation flow with customizable pause times">
                <InfoCircleOutlined className="info-icon" />
              </Tooltip>
            </AIJDVFieldLabel>
            <AIJDVFieldDescription>
              Build the conversation flow with customizable pause times
            </AIJDVFieldDescription>
          </Col>
          <Col>
            <Space size={8}>
              {canExport && id && /<question[\s>]/.test(formData.prompt_questions || '') && (
                <Dropdown menu={{ items: exportMenuItems }} trigger={['click']}>
                  <AIJDVGhostButton icon={<ExportOutlined />}>
                    Export
                  </AIJDVGhostButton>
                </Dropdown>
              )}
              {canImport && (
                <>
                  <AIJDVGhostButton icon={<ImportOutlined />} onClick={() => importFileRef.current?.click()}>
                    Import
                  </AIJDVGhostButton>
                  <input
                    ref={importFileRef}
                    type="file"
                    accept=".txt,.csv,.xls,.xlsx"
                    style={{ display: 'none' }}
                    onChange={handleImportFileChange}
                  />
                </>
              )}
              {canLoadFromSurvey && (
                <AIJDVGhostButton icon={<FileTextOutlined />} onClick={handleOpenSurveyModal}>
                  Load Questions
                </AIJDVGhostButton>
              )}
            </Space>
          </Col>
        </AIJDVFormRowHeader>
        <AIJDVNoMarginFormItem>
          <ThemeForm.Item
            name="prompt_questions"
            rules={[{ required: true, message: 'Please add at least one question' }]}
          >
            <QuestionBuilder
              globalDuration={globalQuestionDuration}
              canEditDuration={canEditQuestionDuration}
              onDurationChange={setGlobalQuestionDuration}
              voiceId={selectedVoice !== null && selectedVoice >= 0 && voices[selectedVoice]?.voice_id ? voices[selectedVoice].voice_id : undefined}
              onPlayStart={handleQuestionPlayStart}
              stopAudioRef={questionBuilderStopRef}
            />
          </ThemeForm.Item>
        </AIJDVNoMarginFormItem>
      </AIJDVFormSection>

      {/* First Message - Moved after Questionnaire */}
      <AIJDVFormSection>
        <AIJDVFieldLabel>
          First Message
          <Tooltip title="The opening message the AI agent will say">
            <InfoCircleOutlined className="info-icon" />
          </Tooltip>
        </AIJDVFieldLabel>
        <AIJDVNoMarginFormItem>
          <ThemeForm.Item
            name="first_message"
            rules={[{ required: true, message: 'Please enter first message' }]}
          >
            <ThemeInput size="large" placeholder="Hi. I'm John Della Volpe's AI interviewing assistant. How are you doing today?" />
          </ThemeForm.Item>
        </AIJDVNoMarginFormItem>
      </AIJDVFormSection>

      {/* Set the Call Tone - Moved after Questionnaire */}
      {canEditCallTone && (
        <AIJDVFormSection>
          <AIJDVFormRowHeader justify="space-between" align="top">
            <Col>
              <AIJDVFieldLabel>
                Set the Call Tone
                <Tooltip title="Define the behavioral instructions for the agent">
                  <InfoCircleOutlined className="info-icon" />
                </Tooltip>
              </AIJDVFieldLabel>
              <AIJDVFieldDescription>
                Define the behavioral instructions for the agent
              </AIJDVFieldDescription>
            </Col>
          </AIJDVFormRowHeader>
          <AIJDVNoMarginFormItem>
            <ThemeForm.Item
              name="prompt_call_tone"
              rules={canEditCallTone ? [{ required: true, message: 'Please enter call tone instructions' }] : []}
            >
              <ThemeInput.TextArea
                rows={10}
                placeholder="Enter the agent's behavioral instructions, speaking style, and interview approach..."
                size="large"
              />
            </ThemeForm.Item>
          </AIJDVNoMarginFormItem>
        </AIJDVFormSection>
      )}

      {/* Closing Note */}
      {canEditClosingNote && (
        <AIJDVFormSection>
          <AIJDVFormRowHeader justify="space-between" align="top">
            <Col>
              <AIJDVFieldLabel>
                Closing Note
                <Tooltip title="Final message after all questions are asked">
                  <InfoCircleOutlined className="info-icon" />
                </Tooltip>
              </AIJDVFieldLabel>
              <AIJDVFieldDescription>
                Final message after all questions are asked
              </AIJDVFieldDescription>
            </Col>
          </AIJDVFormRowHeader>
          <AIJDVNoMarginFormItem>
            <ThemeForm.Item
              name="prompt_closing_note"
              rules={canEditClosingNote ? [{ required: true, message: 'Please enter closing note' }] : []}
            >
              <ThemeInput.TextArea
                rows={4}
                placeholder='After asking these questions, thank them for participating and ask if they have any other questions. If they say no, say "Ok, thanks for chatting today. You may now hang up the phone."'
                size="large"
              />
            </ThemeForm.Item>
          </AIJDVNoMarginFormItem>
        </AIJDVFormSection>
      )}

      {/* Voice Selection Section */}
      <AIJDVFormSection>
        <AIJDVFormRowHeader justify="space-between" align="middle" className="pb-3">
          <Col>
            <AIJDVFieldLabel>
              Choose a Voice
              <Tooltip title="Select a voice for the AI agent">
                <InfoCircleOutlined className="info-icon" />
              </Tooltip>
            </AIJDVFieldLabel>
            <AIJDVFieldDescription>
              Select a voice for your AI agent
            </AIJDVFieldDescription>
          </Col>
          <Col>
            <AIJDVGhostButton onClick={handleOpenVoiceModal}>
              View All
            </AIJDVGhostButton>
          </Col>
        </AIJDVFormRowHeader>
        
        {getVoicesState.apiState === 'loading' && (
          <AIJDVLoadingMessage>Loading voices...</AIJDVLoadingMessage>
        )}
        
        {getVoicesState.apiState === 'success' && voices.length > 0 && (
          <AIJDVVoiceGrid>
            {getDisplayVoices().map((voice) => {
              const originalIndex = voices.findIndex(v => v.voice_id === voice.voice_id);
              const voiceLabels = voice.labels || {};
              const nationality = voiceLabels.accent ? voiceLabels.accent.charAt(0).toUpperCase() + voiceLabels.accent.slice(1) : '';
              const gender = voiceLabels.gender ? voiceLabels.gender.charAt(0).toUpperCase() + voiceLabels.gender.slice(1) : '';
              const initials = voice.name ? voice.name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2) : '?';
              const avatarColor = getAvatarColor(voice.name || '');
              const isSelected = selectedVoice === originalIndex;

              return (
                <AIJDVVoiceCard
                  key={voice.voice_id}
                  className={isSelected ? 'selected' : ''}
                  onClick={() => handleVoiceSelect(originalIndex)}
                >
                  <AIJDVVoiceAvatar>
                    <AIJDVVoiceAvatarPlaceholder $bgColor={avatarColor}>
                      {initials}
                    </AIJDVVoiceAvatarPlaceholder>
                  </AIJDVVoiceAvatar>
                  <AIJDVVoiceInfo>
                    <AIJDVVoiceName className="voice-name">{voice.name}</AIJDVVoiceName>
                    <AIJDVVoiceMeta className="voice-meta">
                      {nationality && gender ? `${nationality}, ${gender}` : nationality || gender || 'Unknown'}
                    </AIJDVVoiceMeta>
                  </AIJDVVoiceInfo>
                  <Tooltip title={voicePlayingId === voice.voice_id ? "Stop" : "Play preview"}>
                    <AIJDVVoiceSpeakerButton
                      className="speaker-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleVoicePreview(voice);
                      }}
                    >
                      {voiceLoadingId === voice.voice_id ? <LoadingOutlined spin /> : voicePlayingId === voice.voice_id ? <PauseOutlined /> : <SoundOutlined />}
                    </AIJDVVoiceSpeakerButton>
                  </Tooltip>
                </AIJDVVoiceCard>
              );
            })}
          </AIJDVVoiceGrid>
        )}

        {getVoicesState.apiState === 'success' && voices.length === 0 && (
          <AIJDVLoadingMessage>No voices available</AIJDVLoadingMessage>
        )}
      </AIJDVFormSection>

      {/* Footer with Save button */}
      <AIJDVFooter>
        {!isEdit ? (
          // New agent creation: Show both buttons (same as modal behavior)
          <>
            <AIJDVSecondaryButton
              onClick={handleSaveAndGoBack}
              loading={
                createPollingAgentState.apiState === 'loading' ||
                updatePollingAgentState.apiState === 'loading'
              }
              disabled={
                createPollingAgentState.apiState === 'loading' ||
                updatePollingAgentState.apiState === 'loading'
              }
            >
              Save and Go Back
            </AIJDVSecondaryButton>
            <AIJDVPrimaryButton
              onClick={handleSaveAndCreateStudy}
              loading={
                createPollingAgentState.apiState === 'loading' ||
                updatePollingAgentState.apiState === 'loading'
              }
              disabled={
                createPollingAgentState.apiState === 'loading' ||
                updatePollingAgentState.apiState === 'loading'
              }
            >
              Save & Create Study
            </AIJDVPrimaryButton>
          </>
        ) : (
          // Edit mode: Show single Update button
          <AIJDVPrimaryButton
            onClick={handleSave}
            loading={
              createPollingAgentState.apiState === 'loading' ||
              updatePollingAgentState.apiState === 'loading'
            }
          >
            Update
          </AIJDVPrimaryButton>
        )}
      </AIJDVFooter>
    </ThemeForm>
  );

  // Render Voice Selection Modal
  const renderVoiceModal = () => (
    <ThemeModal
      title="Choose a Voice"
      open={isVoiceModalVisible}
      onCancel={handleCloseVoiceModal}
      maskClosable={false}
      width={1200}
      footer={[
        <Button key="cancel" onClick={handleCloseVoiceModal}>
          Cancel
        </Button>,
        <Button key="save" type="primary" onClick={handleSaveVoiceSelection}>
          Save
        </Button>,
      ]}
      bodyStyle={{
        padding: '24px',
        display: 'flex',
        flexDirection: 'column',
        maxHeight: '600px',
        height: '600px',
      }}
    >
      {getVoicesState.apiState === 'loading' && (
        <AIJDVLoadingMessage>Loading voices...</AIJDVLoadingMessage>
      )}
      {getVoicesState.apiState === 'success' && voices.length > 0 && (
        <>
          {/* Filters - Fixed at top */}
          <div style={{ marginBottom: '24px', flexShrink: 0 }}>
            <AIJDVVoiceFiltersRow gutter={[16, 16]}>
              <Col xs={24} sm={12} md={6}>
                <AIJDVSelect
                  size="large"
                  value={selectedNationality}
                  onChange={setSelectedNationality}
                  placeholder="Filter by Nationality"
                  options={[
                    { label: 'All Nationalities', value: 'all' },
                    ...Array.from(new Set(voices.map(v => v.labels?.accent).filter(Boolean)))
                      .sort()
                      .map(accent => ({
                        label: accent.charAt(0).toUpperCase() + accent.slice(1),
                        value: accent
                      }))
                  ]}
                />
              </Col>
              <Col xs={24} sm={12} md={6}>
                <AIJDVSelect
                  size="large"
                  value={selectedGender}
                  onChange={setSelectedGender}
                  placeholder="Filter by Gender"
                  options={[
                    { label: 'All Genders', value: 'all' },
                    ...Array.from(new Set(voices.map(v => v.labels?.gender).filter(Boolean)))
                      .sort()
                      .map(gender => ({
                        label: gender.charAt(0).toUpperCase() + gender.slice(1),
                        value: gender
                      }))
                  ]}
                />
              </Col>
            </AIJDVVoiceFiltersRow>
          </div>

          {/* Scrollable Voice Grid */}
          <AIJDVVoiceModalScrollable>
            {filteredVoices.length === 0 && (
              <AIJDVLoadingMessage>No voices found for selected filters</AIJDVLoadingMessage>
            )}

            {filteredVoices.length > 0 && (
              <AIJDVVoiceGrid>
                {filteredVoices.map((voice) => {
                  const originalIndex = voices.findIndex(v => v.voice_id === voice.voice_id);
                  const voiceLabels = voice.labels || {};
                  const nationality = voiceLabels.accent ? voiceLabels.accent.charAt(0).toUpperCase() + voiceLabels.accent.slice(1) : '';
                  const gender = voiceLabels.gender ? voiceLabels.gender.charAt(0).toUpperCase() + voiceLabels.gender.slice(1) : '';
                  const initials = voice.name ? voice.name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2) : '?';
                  const avatarColor = getAvatarColor(voice.name || '');
                  const isSelected = tempSelectedVoice === originalIndex;

                  return (
                    <AIJDVVoiceCard
                      key={voice.voice_id}
                      className={isSelected ? 'selected' : ''}
                      onClick={() => handleVoiceSelectInModal(originalIndex)}
                    >
                      <AIJDVVoiceAvatar>
                        <AIJDVVoiceAvatarPlaceholder $bgColor={avatarColor}>
                          {initials}
                        </AIJDVVoiceAvatarPlaceholder>
                      </AIJDVVoiceAvatar>
                      <AIJDVVoiceInfo>
                        <AIJDVVoiceName className="voice-name">{voice.name}</AIJDVVoiceName>
                        <AIJDVVoiceMeta className="voice-meta">
                          {nationality && gender ? `${nationality}, ${gender}` : nationality || gender || 'Unknown'}
                        </AIJDVVoiceMeta>
                      </AIJDVVoiceInfo>
                      <Tooltip title={voicePlayingId === voice.voice_id ? "Stop" : "Play preview"}>
                        <AIJDVVoiceSpeakerButton
                          className="speaker-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleVoicePreview(voice);
                          }}
                        >
                          {voiceLoadingId === voice.voice_id ? <LoadingOutlined spin /> : voicePlayingId === voice.voice_id ? <PauseOutlined /> : <SoundOutlined />}
                        </AIJDVVoiceSpeakerButton>
                      </Tooltip>
                    </AIJDVVoiceCard>
                  );
                })}
              </AIJDVVoiceGrid>
            )}
          </AIJDVVoiceModalScrollable>
        </>
      )}
      {getVoicesState.apiState === 'success' && voices.length === 0 && (
        <AIJDVLoadingMessage>No voices available</AIJDVLoadingMessage>
      )}
    </ThemeModal>
  );

  // Permission check - redirect if no permission for create/edit (must be after all hooks)
  const requiredPermission = isEdit ? 'edit' : 'create';
  if (!can(requiredPermission)) {
    return <Navigate to="/polling-agents" replace />;
  }

  return (
    <>
      <MobHeader />
      <AIJDVPageWrapper>
        {/* Header */}
        <AIJDVHeader>
          <ThemeFilterBtn size="large" icon={<img src="/images/back-icon.svg" alt="Back" />} classNames="btnGrayBorder" onClick={() => navigate('/polling-agents/')}/>

          <AIJDVTitle>AI JDV Create Poll</AIJDVTitle>
          {isEdit && canDuplicate && (
            <AIJDVGhostButton
              icon={<CopyOutlined />}
              onClick={handleCopyAndCreateNew}
              style={{ marginLeft: 'auto' }}
            >
              Copy & Create New
            </AIJDVGhostButton>
          )}
        </AIJDVHeader>

        {/* Single Screen Form with Voice Selection */}
        <div style={{ padding: '24px' }}>
          {renderAgentDetailsTab()}
        </div>
      </AIJDVPageWrapper>

      <ThemeModal
        title="Load Questions"
        open={isSurveyModalVisible}
        onCancel={handleCloseSurveyModal}
        footer={null}
        width={800}
        bodyStyle={{ padding: 0 }}
      >
        <AIJDVModalTabs
          type="card"
          activeKey={modalTab}
          onChange={handleModalTabChange}
        >
          <AIJDVModalTabs.TabPane tab="Surveys" key="surveys">
            <AIJDVModalSearchWrapper>
              <AIJDVModalSearchInput
                placeholder="Search surveys by name..."
                prefix={<SearchOutlined />}
                value={surveySearchText}
                onChange={(e) => setSurveySearchText(e.target.value)}
                size="large"
              />
            </AIJDVModalSearchWrapper>
            <AIJDVModalTableWrapper>
              <ThemeTable
                dataSource={filteredSurveys}
                columns={[
                  {
                    title: 'Survey Name',
                    dataIndex: 'title',
                    key: 'title',
                    ellipsis: true,
                  },
                  {
                    title: 'Questions',
                    dataIndex: 'question_count',
                    key: 'question_count',
                    width: 100,
                    align: 'center',
                  },
                  {
                    title: 'Action',
                    key: 'action',
                    width: 100,
                    align: 'center',
                    render: (_, record) => (
                      <AIJDVModalSelectButton
                        type="primary"
                        size="small"
                        onClick={() => handleSelectSurvey(record)}
                      >
                        Select
                      </AIJDVModalSelectButton>
                    ),
                  },
                ]}
                rowKey="_id"
                scroll={{ y: 350 }}
                pagination={{
                  pageSize: 10,
                  showSizeChanger: false,
                }}
                loading={getSurveysState.apiState === 'loading'}
                locale={{
                  emptyText: surveySearchText
                    ? 'No surveys found matching your search'
                    : 'No surveys available',
                }}
              />
            </AIJDVModalTableWrapper>
          </AIJDVModalTabs.TabPane>
          <AIJDVModalTabs.TabPane tab="AI JDV Agents" key="agents">
            <AIJDVModalSearchWrapper>
              <AIJDVModalSearchInput
                placeholder="Search agents by name..."
                prefix={<SearchOutlined />}
                value={agentSearchText}
                onChange={(e) => setAgentSearchText(e.target.value)}
                size="large"
              />
            </AIJDVModalSearchWrapper>
            <AIJDVModalTableWrapper>
              <ThemeTable
                dataSource={filteredAgents}
                columns={[
                  {
                    title: 'Agent Name',
                    dataIndex: 'agent_name',
                    key: 'agent_name',
                    ellipsis: true,
                  },
                  {
                    title: 'Questions',
                    key: 'question_count',
                    width: 100,
                    align: 'center',
                    render: (_, record) => {
                      const count = (record.prompt_questions?.match(/<question[\s>]/g) || []).length;
                      return count;
                    },
                  },
                  {
                    title: 'Action',
                    key: 'action',
                    width: 100,
                    align: 'center',
                    render: (_, record) => (
                      <AIJDVModalSelectButton
                        type="primary"
                        size="small"
                        onClick={() => handleSelectAgent(record)}
                      >
                        Select
                      </AIJDVModalSelectButton>
                    ),
                  },
                ]}
                rowKey="_id"
                scroll={{ y: 350 }}
                pagination={{
                  pageSize: 10,
                  showSizeChanger: false,
                }}
                loading={getPollingAgentsState.apiState === 'loading'}
                locale={{
                  emptyText: agentSearchText
                    ? 'No agents found matching your search'
                    : 'No agents available',
                }}
              />
            </AIJDVModalTableWrapper>
          </AIJDVModalTabs.TabPane>
        </AIJDVModalTabs>
      </ThemeModal>

      {/* Voice Selection Modal */}
      {renderVoiceModal()}

      {/* Copy & Create New Modal */}
      <ThemeModal
        title="Copy & Create New"
        open={copyModalVisible}
        onCancel={() => {
          setCopyModalVisible(false);
          setCopyAgentName('');
        }}
        onOk={handleCopyConfirm}
        okText="Create"
        okButtonProps={{
          disabled: !copyAgentName.trim(),
        }}
        width={480}
        destroyOnClose
      >
        <div style={{ marginBottom: 8 }}>
          <label>Agent Name</label>
        </div>
        <ThemeInput
          size="large"
          placeholder="Enter agent name"
          value={copyAgentName}
          onChange={(e) => setCopyAgentName(e.target.value)}
          maxLength={100}
          onPressEnter={() => {
            if (copyAgentName.trim()) handleCopyConfirm();
          }}
        />
      </ThemeModal>

      {/* Import Questions Confirmation Modal */}
      <ThemeModal
        title="Import Questions"
        open={importModalVisible}
        onCancel={() => { setImportModalVisible(false); setPendingImport(null); }}
        onOk={handleImportConfirm}
        okText={/<question[\s>]/.test(formData.prompt_questions || '') ? 'Replace Questions' : 'Import Questions'}
        width={480}
        destroyOnClose
      >
        <p>
          {/<question[\s>]/.test(formData.prompt_questions || '')
            ? <>This will replace all existing questions with <strong>{pendingImport?.questions?.length || 0}</strong> question{(pendingImport?.questions?.length || 0) !== 1 ? 's' : ''} from <strong>{pendingImport?.fileName}</strong>.</>
            : <>This will import <strong>{pendingImport?.questions?.length || 0}</strong> question{(pendingImport?.questions?.length || 0) !== 1 ? 's' : ''} from <strong>{pendingImport?.fileName}</strong>.</>
          }
        </p>
      </ThemeModal>
    </>
  );
};

const mapStateToProps = (state) => ({
  createPollingAgentState: state.pollingAgent.createPollingAgentState,
  updatePollingAgentState: state.pollingAgent.updatePollingAgentState,
  getPollingAgentByIdState: state.pollingAgent.getPollingAgentByIdState,
  getPollingAgentsState: state.pollingAgent.getPollingAgentsState,
  getVoicesState: state.pollingAgent.getVoicesState,
  getSurveyDetailState: state.survey.getSurveyDetailState,
  getSurveysState: state.survey.getSurveysState,
});

const mapDispatchToProps = (dispatch) => ({
  createPollingAgent: (params) => dispatch(createPollingAgentApi(params)),
  createPollingAgentReset: () => dispatch(createPollingAgentReset()),
  updatePollingAgent: (params) => dispatch(updatePollingAgentApi(params.id, params)),
  updatePollingAgentReset: () => dispatch(updatePollingAgentReset()),
  getPollingAgentById: (id) => dispatch(getPollingAgentByIdApi(id)),
  getPollingAgentByIdReset: () => dispatch(getPollingAgentByIdReset()),
  getPollingAgents: (params) => dispatch(getPollingAgentsApi(params)),
  getVoices: (params) => dispatch(getVoicesApi(params)),
  getVoicesReset: () => dispatch(getVoicesReset()),
  getSurveyDetail: (surveyId) => dispatch(getSurveyDetailApi(surveyId)),
  getSurveyDetailReset: () => dispatch(getSurveyDetailReset()),
  getSurveys: (params) => dispatch(getSurveysApi(params)),
  getSurveysReset: () => dispatch(getSurveysReset()),
});

export default connect(mapStateToProps, mapDispatchToProps)(CreatePollingAgent);
